import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Type } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MysteryCharacter } from "@/interfaces/mystery";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";

// "Change character names" — a free request card in the Extras tab, NOT a
// product. It sends a normal contact message (submit-contact-form, so it gets
// the same rate limit, honeypot-free signed-in path and support@ alert) and
// Jonathan or Claude makes the rename by hand. Purpose: find out whether
// hosts want bulk renames at all (nobody asks today, possibly because nobody
// knows it is possible) before building an automated action, see the Open
// reminders item for the review point and the passive query.
//
// The card is mounted only while the Extras tab is open (Radix unmounts
// inactive tab content), so `name_change_card_viewed` counts tab opens, and
// `name_change_request_opened` counts clicks; the durable record of real
// intent is the `contact_messages` row with subject "Name change request".

interface NameChangeRequestCardProps {
  packageId: string;
  characters: MysteryCharacter[];
}

const MAX_NAME = 80;

const NameChangeRequestCard: React.FC<NameChangeRequestCardProps> = ({ packageId, characters }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newNames, setNewNames] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    trackEvent("name_change_card_viewed", { package_id: packageId });
  }, [packageId]);

  const knownEmail = user?.email || "";

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      trackEvent("name_change_request_opened", { package_id: packageId });
    } else {
      setNewNames({});
      setNote("");
    }
  };

  const handleSubmit = async () => {
    const changes = characters
      .map((c) => ({ from: c.character_name, to: (newNames[c.id] || "").trim() }))
      .filter((c) => c.to.length > 0 && c.to !== c.from);

    if (changes.length === 0) {
      toast.error(t("nameChange.toasts.noChanges"));
      return;
    }

    // Two characters cannot end up with the same name, nor take the name of a
    // character that is staying as it is.
    const finalNames = characters.map((c) => {
      const to = (newNames[c.id] || "").trim();
      return (to.length > 0 ? to : c.character_name).toLowerCase();
    });
    if (new Set(finalNames).size !== finalNames.length) {
      toast.error(t("nameChange.toasts.duplicate"));
      return;
    }

    const replyEmail = (knownEmail || email).trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(replyEmail)) {
      toast.error(t("nameChange.toasts.invalidEmail"));
      return;
    }

    setSubmitting(true);
    try {
      const lines = [
        "Name change request (Extras tab, free request card)",
        `Package: ${packageId}`,
        `Email: ${replyEmail}`,
        `Language: ${i18n.language || "en"}`,
        "",
        "Changes:",
        ...changes.map((c) => `- ${c.from} -> ${c.to}`),
      ];
      if (note.trim()) lines.push("", `Note: ${note.trim()}`);

      const displayName =
        (user as { name?: string } | null)?.name?.trim() || replyEmail.split("@")[0] || "Host";

      const { data: result, error } = await supabase.functions.invoke("submit-contact-form", {
        body: {
          name: displayName.slice(0, 200),
          email: replyEmail,
          subject: "Name change request",
          message: lines.join("\n"),
          language: i18n.language || "en",
          website: "",
        },
      });
      if (error || !result?.ok) {
        throw new Error(result?.error || error?.message || "unknown");
      }

      trackEvent("name_change_request_submitted", { package_id: packageId, count: changes.length });
      toast.success(t("nameChange.toasts.sent"));
      handleOpenChange(false);
    } catch (e) {
      console.error("Error sending name change request:", e);
      trackEvent("name_change_request_error", {
        package_id: packageId,
        error: e instanceof Error ? e.message : "unknown",
      });
      toast.error(t("nameChange.toasts.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Type className="h-5 w-5" />
          <h3 className="text-lg font-semibold">{t("nameChange.card.title")}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t("nameChange.card.teaser")}</p>
        <Button onClick={() => handleOpenChange(true)}>{t("nameChange.card.cta")}</Button>
      </div>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Type className="h-5 w-5" />
                {t("nameChange.dialog.title")}
              </DialogTitle>
            </DialogHeader>

            <p className="text-sm text-muted-foreground">{t("nameChange.dialog.subtitle")}</p>

            <div className="space-y-3">
              {characters.map((c) => (
                <div key={c.id} className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-3 sm:items-center">
                  <Label htmlFor={`new-name-${c.id}`} className="text-sm font-medium break-words">
                    {c.character_name}
                  </Label>
                  <Input
                    id={`new-name-${c.id}`}
                    value={newNames[c.id] || ""}
                    maxLength={MAX_NAME}
                    placeholder={t("nameChange.dialog.newNamePlaceholder")}
                    onChange={(e) => setNewNames((prev) => ({ ...prev, [c.id]: e.target.value }))}
                  />
                </div>
              ))}
            </div>

            <div className="space-y-1">
              <Label htmlFor="name-change-note" className="text-sm">
                {t("nameChange.dialog.noteLabel")}
              </Label>
              <Textarea
                id="name-change-note"
                value={note}
                maxLength={1000}
                rows={3}
                placeholder={t("nameChange.dialog.notePlaceholder")}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            {!knownEmail && (
              <div className="space-y-1">
                <Label htmlFor="name-change-email" className="text-sm">
                  {t("nameChange.dialog.emailLabel")}
                </Label>
                <Input
                  id="name-change-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            )}

            <p className="text-xs text-muted-foreground">{t("nameChange.dialog.timing")}</p>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 border-2 border-white/30 hover:border-white/50 hover:bg-white/5"
                onClick={() => handleOpenChange(false)}
                disabled={submitting}
              >
                {t("nameChange.dialog.cancel")}
              </Button>
              <Button className="flex-1" onClick={handleSubmit} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {t("nameChange.dialog.submit")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NameChangeRequestCard;
